import $i18n from 'panda-i18n';
import React, { createContext, useCallback, useMemo, useRef, useState, } from 'react';
import isObject from 'lodash/isObject';
import { getRequestService } from '@cainiaofe/cn-ui-utils';
import { defaultOssUploadService } from '@/components/upload-core/constant';
import { fileToObject, fileValidator, getUploadData as _getUploadData, removeFileItem, } from '@/components/upload-core/utils/utils';
import { CnMessage } from '@/components/cn-message';
import { useLatest, useUpdateEffect } from 'ahooks';
export const UploadStateContext = createContext({});
export const UploadContext = (props) => {
    const { children } = props;
    const { onError, beforeUpload: _beforeUpload, defaultValue, value: originValue, onChange: _onChange, accept, multiple = false, showRemove = true, readOnly = false, onRemove: _onRemove, onPreview, showDownload = true, onDownload = (file) => {
        if (file.downloadUrl || file.url) {
            window.open(file.downloadUrl || file.url);
        }
    }, limit = 9, sizeLimit, uploadConfirm, } = props;
    const serviceRef = useLatest(props.service || defaultOssUploadService);
    const requestConfigRef = useLatest(props.requestConfig);
    const withCredentials = requestConfigRef.current?.withCredentials;
    const [value, setValue] = useState(originValue || defaultValue || []);
    const onChangeRef = useRef(_onChange);
    useUpdateEffect(() => {
        onChangeRef.current = _onChange;
    }, [_onChange]);
    // 是否是受控模式(value由外部控制)
    const isControlled = 'value' in props;
    useUpdateEffect(() => {
        if (!isControlled)
            return;
        setValue((prev) => {
            if (!originValue || !Array.isArray(originValue))
                return [];
            const _value = originValue?.map((item) => {
                const prevItem = prev.find(({ key }) => key === item.key);
                return { ...prevItem, ...item };
            });
            return _value || [];
        });
    }, [originValue]);
    // 缓存上传凭证信息
    const cacheOSSTokenMapRef = useRef({});
    const cacheFileListRef = useRef({});
    const onRemove = useCallback((_file) => {
        if (_onRemove) {
            _onRemove(_file);
            return;
        }
        setValue((prev) => removeFileItem(_file, prev));
    }, [_onRemove]);
    const onChange = useCallback((fileList, file) => {
        if (onChangeRef.current) {
            const _simpleValue = fileList.map((item) => {
                const ossData = cacheOSSTokenMapRef.current[item.key];
                const result = {
                    key: ossData?.key || item.key,
                    name: item.name,
                    size: item.size,
                    url: ossData?.url || item.url,
                };
                cacheFileListRef.current[result.key] = {
                    ...cacheFileListRef.current[result.key],
                    ...item,
                    ...result,
                    status: 'done',
                };
                return result;
            });
            onChangeRef.current(_simpleValue, file);
        }
        if (!isControlled) {
            setValue(fileList);
        }
    }, [isControlled]);
    const sizeLimitValidate = useCallback((_file) => {
        if (!sizeLimit || !_file.size)
            return;
        if (_file.size <= sizeLimit * 1024 * 1024)
            return;
        const errMsg = $i18n.get({
            id: 'PleaseSelectAnAttachmentSmallerThan_1347731510',
            dm: '请选择小于{sizeLimit}M的附件',
            ns: 'CnOssUpload',
        }, { sizeLimit });
        CnMessage.error(errMsg);
        throw Error(errMsg);
    }, [sizeLimit]);
    const requestConfig = useMemo(() => {
        const service = serviceRef.current;
        if (requestConfigRef.current)
            return requestConfigRef.current;
        if (typeof service === 'function') {
            return { service };
        }
        return service;
    }, [requestConfigRef, serviceRef]);
    const requestService = useMemo(() => getRequestService(requestConfig), [requestConfig]);
    const fetchOssToken = useCallback(async (_file) => {
        try {
            if (typeof serviceRef.current === 'function') {
                return await serviceRef.current({ file: _file });
            }
            if (!requestService)
                return;
            return await requestService({ fileName: _file.name });
        }
        catch (err) {
            const tempFile = fileToObject(_file, { status: 'error' });
            setValue((prev) => [...prev, { ...tempFile, errorMsg: err.message }]);
            throw err;
        }
    }, [serviceRef, requestService]);
    const beforeUpload = useCallback(async (rcFile, fileList) => {
        const _file = fileToObject(rcFile, { status: 'idle' });
        sizeLimitValidate(_file);
        try {
            let tempFile = rcFile;
            if (_beforeUpload) {
                const result = _beforeUpload(rcFile, fileList);
                if (result === false)
                    throw new Error();
                if (isObject(result) && result.then) {
                    const temp = await result;
                    if (temp)
                        tempFile = temp;
                }
            }
            fileValidator(tempFile);
            const data = await fetchOssToken(tempFile);
            if (data)
                cacheOSSTokenMapRef.current[_file.key] = data;
            return tempFile;
        }
        catch (error) {
            if (onError) {
                onError(error, null, fileToObject(rcFile, { status: 'error' }));
            }
            throw error;
        }
    }, [_beforeUpload, fetchOssToken, onError, sizeLimitValidate]);
    const getUploadAction = useCallback(async (file) => {
        return cacheOSSTokenMapRef.current[file.uid]?.host;
    }, []);
    const getUploadData = useCallback((file) => {
        return _getUploadData(cacheOSSTokenMapRef.current[file.uid]);
    }, []);
    const getFileFromCache = useCallback((file) => {
        return cacheFileListRef.current[file.key] || file;
    }, []);
    const output = useMemo(() => {
        return {
            value,
            setValue,
            limited: value.length >= limit,
            limit,
            accept,
            multiple,
            readOnly,
            onChange,
            showRemove,
            onRemove,
            onError,
            beforeUpload,
            onPreview,
            getUploadAction,
            getUploadData,
            showDownload,
            onDownload,
            withCredentials,
            getFileFromCache,
            uploadConfirm,
        };
    }, [
        accept,
        beforeUpload,
        getUploadAction,
        getUploadData,
        limit,
        multiple,
        onChange,
        onError,
        onPreview,
        onRemove,
        readOnly,
        showRemove,
        value,
        showDownload,
        onDownload,
        withCredentials,
        getFileFromCache,
        uploadConfirm,
    ]);
    return (React.createElement(UploadStateContext.Provider, { value: output }, children));
};
